import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';

import type { WritingCard } from '../../models';

@Component({
  selector: 'app-writing',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './writing.component.html',
  styleUrl: './writing.component.scss',
  imports: [RouterLink]
})
export class WritingComponent {
  protected readonly clbCards = [
    { level: 'CLB 6', score: '7-8-9', cefr: 'B1', color: 'var(--clb-6)', minHeight: '4.5rem', textLight: false },
    { level: 'CLB 7', score: '10-11', cefr: 'B2', color: 'var(--clb-7)', minHeight: '5rem', textLight: false },
    { level: 'CLB 8', score: '12-13', cefr: 'B2', color: 'var(--clb-8)', minHeight: '5.5rem', textLight: false },
    { level: 'CLB 9', score: '14-15', cefr: 'C1', color: 'var(--clb-9)', minHeight: '6rem', textLight: true },
    { level: 'CLB 10+', score: '16-20', cefr: 'C1-C2', color: 'var(--clb-10)', minHeight: '6.5rem', textLight: true }
  ] as const;

  protected getProgressPercent(card: WritingCard): number {
    if (card.totalCount == null || card.totalCount === 0) return 0;
    return Math.round(((card.completedCount ?? 0) / card.totalCount) * 100);
  }
  protected readonly writingTips: string[] = [
    '注意字数要求，任务 1 约 60–120 字，任务 2/3 按题目说明。',
    '先列提纲再动笔，能减少跑题并加快成文速度。',
    '多用连接词和过渡句，让结构更清晰、逻辑更连贯。',
    '写完后留 2–3 分钟检查拼写、语法和标点。'
  ];

  protected readonly cards: WritingCard[] = [
    {
      id: 'tache1',
      title: '任务 1',
      description:
        '编写一条消息来描述、讲述和/或解释，发送给一个或多个收件人，其状态已在说明中指定。',
      badge: '基础',
      badgeClass: 'bg-blue-100 text-blue-800',
      iconPath:
        'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
      iconViewBox: '0 0 24 24',
      ctaLabel: '查看题目',
      ctaRoute: '/writing/tache1',
      ctaPrimary: true,
      taskRequirement: '编写一条消息来描述、讲述和/或解释，发送给一个或多个收件人，其状态已在说明中指定。',
      wordCount: '最少60个字/最多120个字',
      timeSuggest: '10 分钟',
      commonTopics: ['邮件', '便条', '邀请', '道歉', '请求', '通知', '日程安排', '表格填写'],
      completedCount: 0,
      totalCount: 24
    },
    {
      id: 'tache2',
      title: '任务 2',
      description:
        '为几位收件人写一篇文章、一封信、一张便条等，提供一份经历报告或一个故事。报告和故事将附有评论、意见或论点，具体取决于目标（例如：主张、调和等）。',
      badge: '进阶',
      badgeClass: 'bg-blue-100 text-blue-800',
      iconPath:
        'M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.157.166 2.42.053 4.1.128v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072m3.5-10.75h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375c-.621 0-1.125-.504-1.125-1.125v-9.75c0-.621.504-1.125 1.125-1.125Zm9 0h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375c-.621 0-1.125-.504-1.125-1.125v-9.75c0-.621.504-1.125 1.125-1.125Z',
      iconViewBox: '0 0 24 24',
      ctaLabel: '查看题目',
      ctaRoute: '/writing/tache2',
      ctaPrimary: true,
      taskRequirement: '为几位收件人写一篇文章、一封信、一张便条等，提供一份经历报告或一个故事。报告和故事将附有评论、意见或论点，具体取决于目标（例如：主张、调和等）。',
      wordCount: '最少120字/最多150字',
      timeSuggest: '20 分钟',
      commonTopics: ['书信', '经历报告', '故事叙述', '评论', '意见表达', '主张', '调和', '议论文'],
      completedCount: 0,
      totalCount: 18
    },
    {
      id: 'tache3',
      title: '任务 3',
      description:
        '写一篇文章（为报纸、网站、同事、上级等），比较两份文档中表达的关于社会事实的两种观点。候选人对两份文件所涵盖的主题发表了自己的看法。',
      badge: '高级',
      badgeClass: 'bg-blue-100 text-blue-800',
      iconPath:
        'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125',
      iconViewBox: '0 0 24 24',
      ctaLabel: '查看题目',
      ctaRoute: '/writing/tache3',
      ctaPrimary: true,
      taskRequirement: '写一篇文章（为报纸、网站、同事、上级等），比较两份文档中表达的关于社会事实的两种观点。候选人对两份文件所涵盖的主题发表了自己的看法。',
      wordCount: '最少120字/最多180字',
      timeSuggest: '30 分钟',
      commonTopics: ['社会事实', '观点比较', '对比分析', '报纸文章', '网站评论', '职场议题', '双文档论证'],
      completedCount: 0,
      totalCount: 12
    }
  ];

  protected get questionBankCards(): WritingCard[] {
    return this.cards;
  }
}
